// The mark scales (R1981, R1992, R3246, arcade.md §7.2,
// ui-map-interface.md §6.1.9).
//
// The mark scale is the size of the fleets and the torpedoes on the map;
// the map and every range keep their size. Two figures of the settings
// blob hold it, one for each mode: `markScaleArcade` for the arcade page
// and `markScaleCore` for the season map. Each is a whole percent on a step
// of the slider of `[presentation.mark_scale]`, 100 by default. The account
// keeps both, so the choice follows the player.
//
// Until R1992 the arcade value was a percent on the device alone, in the
// key `ap.arcade.markscale`. `moveDeviceMarkScale` moves it into the blob
// once, snapped to the slider, and the key goes. The key goes through
// `ui/localstore.ts`, because a private window or a blocked store throws.

import { MARK_SCALE } from '../gen/presentation';
import { readStore, removeStore } from '../ui/localstore';
import {
  MARK_PCT_OWN, loadSettings, onSettingsWrite, saveSettings, snapMarkPct, type GameSettings,
} from '../ui/settings';

/** The two modes that keep a mark scale. */
export type MarkMode = 'arcade' | 'core';

/** The blob key of the mark scale of each mode. */
export const MARK_KEYS = { arcade: 'markScaleArcade', core: 'markScaleCore' } as const satisfies
  Record<MarkMode, keyof GameSettings>;

/** The device key of the R1981 percent, which the first read moves. */
export const DEVICE_MARK_KEY = 'ap.arcade.markscale';

/** The percent a mode draws under some settings. */
export function markPct(mode: MarkMode, s: GameSettings): number {
  return s[MARK_KEYS[mode]];
}

/** The factor the layers take for a mode under some settings: its
 *  percent over 100, so the own size is 1. */
export function markFactor(mode: MarkMode, s: GameSettings): number {
  return markPct(mode, s) / MARK_PCT_OWN;
}

/**
 * Moves the R1981 device percent into the blob, once (R1992, R3246). A
 * percent under 100 sets `markScaleArcade` to its step of the slider,
 * while the blob holds the own size; any other value moves nothing. The
 * device key goes in all cases, so a later read finds nothing. It answers
 * true when it wrote the blob.
 */
export function moveDeviceMarkScale(): boolean {
  const raw = readStore(DEVICE_MARK_KEY);
  if (raw === null) return false;
  removeStore(DEVICE_MARK_KEY);
  const pct = raw.trim() === '' ? NaN : Number(raw);
  if (!Number.isFinite(pct) || pct >= 100) return false;
  const s = loadSettings();
  if (s.markScaleArcade !== MARK_PCT_OWN) return false;
  saveSettings({ ...s, markScaleArcade: snapMarkPct(pct) });
  return true;
}

/** The step after `pct` that a press of the VIEW tile moves to (R3246):
 *  one step smaller, and from the smallest step to the largest. */
export function nextMarkPct(pct: number): number {
  const { minPct, maxPct, stepPct } = MARK_SCALE;
  const at = snapMarkPct(pct);
  return at <= minPct ? maxPct : at - stepPct;
}

/** Moves the mark scale of a mode one step on (`nextMarkPct`) and stores
 *  the blob. It answers the settings it stored. */
export function stepMarkScale(mode: MarkMode): GameSettings {
  const s = loadSettings();
  const key = MARK_KEYS[mode];
  const next = { ...s, [key]: nextMarkPct(s[key]) };
  saveSettings(next);
  return next;
}

/**
 * Calls `fn` with the factor of a mode after each write of the blob that
 * changes it, from any surface of the page. It returns the function that
 * removes `fn`.
 */
export function onMarkScale(mode: MarkMode, fn: (k: number) => void): () => void {
  let last = markFactor(mode, loadSettings());
  return onSettingsWrite((s) => {
    const k = markFactor(mode, s);
    if (k === last) return;
    last = k;
    fn(k);
  });
}
